/** @jsxImportSource @emotion/react */
import Page from '@atlaskit/page';
import PageHeader from '@atlaskit/page-header';
import { css, jsx } from '@emotion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { colors, gridSize } from '@atlaskit/theme';
import { e100 } from '@atlaskit/theme/elevation';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'components/i18n';
import { Box } from '@chakra-ui/react';
import { Outlet, useNavigate, useParams } from 'react-router';
import Skeleton from 'react-loading-skeleton';
import { useIntl } from 'react-intl-next';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CompanyAvatar } from '../../components/avatar';
import { CompanyOption } from '../../components/select/CompanyOption';
import { Customer } from './types';
import api from '../../libs/api';
import { PageWrapper } from '../../components/page';
import TablePage from '../../components/page/TablePage';
import SearchBar from '../../components/SearchBar';
import { searchProperties } from '../../utils';

const truncateText = css`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const loadingData = Array.from({ length: 10 }).map((_, i) => ({ id: i.toString(), name: 'Loading', type: 'business' })) as Customer[];
const Row = ({ index, style, data }) => {
  const customer = data.customers[index];
  const {id} = data;
  console.log('data', data[index]);
  const isLoading = customer?.name === 'Loading';

  return (
    <Link
      to={isLoading? '#': `${customer.id}/edit`}
      style={style}
      key={customer.id}
      css={css`text-decoration: none;
        color: inherit;

        :hover, :focus, :active {
            color: inherit;
        }`}
    >
      <Box
        borderBottom="1px solid"
        borderBottomColor={colors.N20}
        css={css`
            padding: ${gridSize() * 1.5}px 8px;
            display: flex;
            flex-direction: row;
            align-items: center;
            min-width: 0;
            font-size: 0.85rem;
            cursor: pointer;
            color: inherit;
            background-color: ${id === customer.id ? colors.G50 : 'transparent'};

            :hover {
                background-color: ${id === customer.id ? colors.G50 : colors.N10};
            }
        }
        `}
      >
        {isLoading ? <Skeleton count={1} height="2rem" width="2rem" css={css`border-radius: 500px`} /> :
        <CompanyAvatar company={customer} />}
        <Box css={css`
            margin-left: ${gridSize()}px;
            min-width: 0;
            width: 100%;
        `}
        >
          {isLoading ? <Skeleton count={1} height="0.8rem" width="66%" /> : (
            <Box css={truncateText}>
              {customer.name}
              {customer.customerNumber && ` (${customer.customerNumber})`}
            </Box>
          )}
          {/* <div */}
          {/* css={css`color: ${colors.subtleText()}; ${truncateText}`} */}
          {/* >{customer.contactName}</div> */}
        </Box>
      </Box>
    </Link>
  );
};
const CustomersPage: React.FC = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const fetchCustomers = React.useCallback(async (key) => api.get('/Customers', {
    params: {
      filter: {
        order: 'name',
      },
    },
  }), []);

  const {data, error, isLoading, isFetching } = useQuery(['customers'], fetchCustomers);
  let customers: Customer[] = data;
  useEffect(() => {
    if (data && data.length > 0 && !id) {
      navigate(`${data[0].id}/edit`);
    }
  }, [data]);


  if (isLoading) {
    customers = loadingData;
  }

  customers = useMemo(() => {
    if (!query) return customers;
    const q = query.toLowerCase();
    return customers.filter(customer => searchProperties(customer, q));
  }, [query, customers]);

  const intl = useIntl();

  const listData = useMemo(() => ({ customers, id }), [customers, id]);

  const searchLabel = intl.formatMessage({
    id: 'sale.CustomersPage.search',
    defaultMessage: 'Search',
    description: 'Search placeholder',
  });


  const actionsContent = React.useMemo(() => (
    <ButtonGroup>
      <Link to="new">
        <Button
          // appearance="primary"
          onClick={() => null}
        >
          <FormattedMessage
            id="sale.CustomersPage.create"
            defaultMessage="Create customer"
          />
        </Button>
      </Link>
    </ButtonGroup>
  ), []);

  return  (
    <TablePage>
      <Box display="flex" flexDirection="column" height="100vh" position="relative">
        <PageHeader
      // breadcrumbs={breadcrumbs}
          actions={actionsContent}
        >
          Customers
        </PageHeader>
        <Box width="360px">
          <SearchBar
            placeholder={searchLabel}
            value={query || ''}
            onChange={setQuery}
            onReset={() => setQuery('')}
          />
        </Box>
        <Box display='flex' flexDirection="row" flex={1} height="0px">
          <Box width="360px" overflowY="scroll" display="flex">
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
            >
              <Box height={1} />
              <Box flex={1}>
                <AutoSizer>
                  {({ height, width }) =>
                    (
                      <List
                        width={width}
                        height={height}
                        itemSize={61}
                        itemCount={customers.length}
                        itemData={listData}
                      >
                        {Row}
                      </List>
                    )}
                </AutoSizer>
              </Box>
            </Box>
          </Box>
          <Outlet />
        </Box>

      </Box>
    </TablePage>
  );
};

export default CustomersPage;
